import React, { useEffect, useState } from 'react'
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, writeBatch } from 'firebase/firestore'
import { db } from '../../firebase.config'
import { useAuth } from '../../context/AuthContext'
import AppLayout from '../../components/layout/AppLayout'

interface Crew {
  id: string
  name: string
  orgId: string
  createdAt: string
}

interface OrgUser {
  id: string
  name: string
  email: string
  role: string
  crewId?: string
}

export default function CrewManagementPage() {
  const { orgId, isAdmin } = useAuth()
  const [crews, setCrews] = useState<Crew[]>([])
  const [users, setUsers] = useState<OrgUser[]>([])
  const [selectedCrew, setSelectedCrew] = useState<Crew | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [newCrewName, setNewCrewName] = useState('')
  const [creating, setCreating] = useState(false)
  const [movingUser, setMovingUser] = useState<string | null>(null)
  const [showAddUsers, setShowAddUsers] = useState(false)

  useEffect(() => {
    if (!orgId) return
    const unsubCrews = onSnapshot(
      query(collection(db, 'organizations', orgId, 'crews')),
      snap => setCrews(snap.docs.map(d => ({ id: d.id, ...d.data() } as Crew)))
    )
    const unsubUsers = onSnapshot(
      query(collection(db, 'organizations', orgId, 'users'), where('orgId', '==', orgId)),
      snap => setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() } as OrgUser)))
    )
    return () => { unsubCrews(); unsubUsers() }
  }, [orgId])

  const handleCreateCrew = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!orgId || !newCrewName.trim()) return
    setCreating(true)
    try {
      await addDoc(collection(db, 'organizations', orgId, 'crews'), {
        name: newCrewName.trim(),
        orgId,
        createdAt: new Date().toISOString(),
      })
      setShowCreate(false)
      setNewCrewName('')
    } finally {
      setCreating(false)
    }
  }

  const handleDeleteCrew = async (crew: Crew) => {
    if (!window.confirm(`Delete crew "${crew.name}"? Users will be unassigned but not deleted.`)) return
    // Unassign all users from this crew first
    const batch = writeBatch(db)
    const crewUsers = users.filter(u => u.crewId === crew.id)
    crewUsers.forEach(u => {
      batch.update(doc(db, 'organizations', orgId, 'users', u.id), { crewId: '' })
    })
    batch.delete(doc(db, 'organizations', orgId, 'crews', crew.id))
    await batch.commit()
    if (selectedCrew?.id === crew.id) setSelectedCrew(null)
  }

  const handleMoveUser = async (userId: string, newCrewId: string) => {
    await updateDoc(doc(db, 'organizations', orgId, 'users', userId), {
      crewId: newCrewId,
      updatedAt: new Date().toISOString(),
    })
    setMovingUser(null)
  }

  const handleRemoveFromCrew = async (userId: string) => {
    await updateDoc(doc(db, 'organizations', orgId, 'users', userId), {
      crewId: '',
      updatedAt: new Date().toISOString(),
    })
  }

  const crewUsers = (crewId: string) => users.filter(u => u.crewId === crewId)
  const unassignedUsers = users.filter(u => !u.crewId && u.role !== 'admin')
  const otherCrewUsers = (excludeCrewId: string) => users.filter(u => u.crewId && u.crewId !== excludeCrewId && u.role !== 'admin')

  if (!isAdmin()) {
    return (
      <AppLayout title="Crew Management">
        <p className="text-gray-500 text-sm">You do not have permission to manage crews.</p>
      </AppLayout>
    )
  }

  return (
    <AppLayout title="Crew Management">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-500">Create crews and assign users to organize your workforce.</p>
        <button onClick={() => setShowCreate(true)}
          className="bg-accent text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-700">
          + New Crew
        </button>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{crews.length}</p>
          <p className="text-sm text-gray-500">Total Crews</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{users.filter(u => u.crewId).length}</p>
          <p className="text-sm text-gray-500">Assigned Users</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{unassignedUsers.length}</p>
          <p className="text-sm text-gray-500">Unassigned Users</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Crew list */}
        <div className="col-span-1 space-y-3">
          <h3 className="text-sm font-semibold text-gray-600">Crews</h3>
          {crews.length === 0 && (
            <p className="text-sm text-gray-400 italic bg-white rounded-xl border border-gray-200 p-4">No crews created yet.</p>
          )}
          {crews.map(crew => (
            <div key={crew.id}
              className={`bg-white rounded-xl border shadow-sm cursor-pointer transition-all ${selectedCrew?.id === crew.id ? 'border-accent ring-1 ring-accent' : 'border-gray-200 hover:border-gray-300'}`}
              onClick={() => setSelectedCrew(crew)}>
              <div className="px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">{crew.name}</p>
                  <p className="text-xs text-gray-500">{crewUsers(crew.id).length} members</p>
                </div>
                <button onClick={(e) => { e.stopPropagation(); handleDeleteCrew(crew) }}
                  className="text-gray-300 hover:text-red-500 text-sm">🗑</button>
              </div>
            </div>
          ))}
        </div>

        {/* Crew detail */}
        <div className="col-span-2">
          {!selectedCrew ? (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center text-gray-400 text-sm">
              Select a crew to view and manage its members.
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">{selectedCrew.name}</h3>
                  <p className="text-sm text-gray-500">{crewUsers(selectedCrew.id).length} members</p>
                </div>
                <button onClick={() => setShowAddUsers(true)}
                  className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-200">
                  + Add Members
                </button>
              </div>

              {crewUsers(selectedCrew.id).length === 0 ? (
                <div className="p-6 text-center text-gray-400 text-sm">No members in this crew.</div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {crewUsers(selectedCrew.id).map(u => (
                    <div key={u.id} className="flex items-center justify-between px-6 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium">
                          {(u.name || '?')[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{u.name}</p>
                          <p className="text-xs text-gray-500">{u.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.role === 'foreman' ? 'bg-amber-100 text-amber-700' : u.role === 'supervisor' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                          {u.role}
                        </span>
                        <select
                          value={u.crewId || ''}
                          onChange={e => handleMoveUser(u.id, e.target.value)}
                          className="border border-gray-200 rounded px-2 py-1 text-xs bg-white">
                          {crews.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <button onClick={() => handleRemoveFromCrew(u.id)}
                          className="text-gray-300 hover:text-red-500 text-xs">Remove</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create crew modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Create New Crew</h2>
              <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
            </div>
            <form onSubmit={handleCreateCrew} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Crew Name</label>
                <input type="text" value={newCrewName} onChange={e => setNewCrewName(e.target.value)} required
                  placeholder="e.g., Crew Alpha, Crew 1, Team A..."
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg font-medium hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={creating}
                  className="flex-1 bg-accent text-white py-2.5 rounded-lg font-medium hover:bg-amber-700 disabled:bg-amber-300">
                  {creating ? 'Creating...' : 'Create Crew'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add members modal */}
      {showAddUsers && selectedCrew && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Add Members to {selectedCrew.name}</h2>
              <button onClick={() => setShowAddUsers(false)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
            </div>
            <div className="p-6 space-y-3 max-h-80 overflow-y-auto">
              {otherCrewUsers(selectedCrew.id).length === 0 && unassignedUsers.length === 0 && (
                <p className="text-sm text-gray-400 italic text-center">All users are already in a crew.</p>
              )}
              {otherCrewUsers(selectedCrew.id).map(u => (
                <div key={u.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{u.name}</p>
                    <p className="text-xs text-gray-500">{u.email} • {u.crewId ? `Currently in crew` : 'Unassigned'}</p>
                  </div>
                  <button onClick={() => { handleMoveUser(u.id, selectedCrew.id); setShowAddUsers(false) }}
                    className="text-sm bg-accent text-white px-3 py-1 rounded-lg hover:bg-amber-700">
                    Assign
                  </button>
                </div>
              ))}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button onClick={() => setShowAddUsers(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}
